import type { FC, ReactNode } from "react";
import { AppWidget, type PredefinedAppIcon } from "./AppWidget";

/* ==========================================================================
   AppWidgetList - Spotify-style list items
   ========================================================================== */

export type ListItemAction = {
  /** Icon to display (currently supports "plus") */
  icon: "plus";
  /** Click handler */
  onClick?: () => void;
  /** Accessible label */
  label?: string;
};

export type ListItem = {
  /** Item image URL */
  image?: string;
  /** Primary title */
  title: string;
  /** Secondary subtitle */
  subtitle?: string;
  /** Optional action button */
  action?: ListItemAction;
  /** Optional link URL - makes the item clickable */
  href?: string;
};

export type AppWidgetListProps = {
  /** App name displayed in header */
  appName: string;
  /** App icon: predefined key, URL string, or omit for letter avatar */
  appIcon?: PredefinedAppIcon | string;
  /** List items to display */
  items: ListItem[];
  /** Optional className */
  className?: string;
};

/**
 * Plus icon SVG
 */
const PlusIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="app-widget-list__action-icon"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

/**
 * Action icons map
 */
const ACTION_ICONS: Record<ListItemAction["icon"], FC> = {
  plus: PlusIcon,
};

/**
 * Single list item component
 */
const ListItemRow: FC<{ item: ListItem }> = ({ item }) => {
  const ActionIcon = item.action ? ACTION_ICONS[item.action.icon] : null;

  const content = (
    <>
      {item.image && (
        <img src={item.image} alt="" className="app-widget-list__item-image" />
      )}
      <div className="app-widget-list__item-text">
        <span className="app-widget-list__item-title">{item.title}</span>
        {item.subtitle && (
          <span className="app-widget-list__item-subtitle">
            {item.subtitle}
          </span>
        )}
      </div>
      {item.action && ActionIcon && (
        <button
          className="app-widget-list__action"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            item.action?.onClick?.();
          }}
          aria-label={item.action.label || "Action"}
        >
          <ActionIcon />
        </button>
      )}
    </>
  );

  if (item.href) {
    return (
      <a
        href={item.href}
        className="app-widget-list__item app-widget-list__item--link"
        target="_blank"
        rel="noopener noreferrer"
      >
        {content}
      </a>
    );
  }

  return <div className="app-widget-list__item">{content}</div>;
};

/**
 * AppWidgetList - Renders a list of items (like Spotify playlists)
 *
 * @example
 * <AppWidgetList
 *   appName="Spotify"
 *   appIcon="spotify"
 *   items={[
 *     {
 *       image: "https://...",
 *       title: "Top 100 most streamed songs",
 *       subtitle: "hoolgerssson • Playlist",
 *       action: { icon: "plus", onClick: () => {} }
 *     }
 *   ]}
 * />
 */
export const AppWidgetList: FC<AppWidgetListProps> = ({
  appName,
  appIcon,
  items,
  className = "",
}) => {
  return (
    <AppWidget appName={appName} appIcon={appIcon} className={className}>
      <div className="app-widget-list">
        {items.map((item, index) => (
          <ListItemRow key={index} item={item} />
        ))}
      </div>
    </AppWidget>
  );
};

/* ==========================================================================
   AppWidgetImage - Figma-style image preview
   ========================================================================== */

export type ImageButton = {
  /** Button label */
  label: string;
  /** Click handler */
  onClick?: () => void;
  /** Button variant */
  variant?: "primary" | "secondary";
};

export type ImageItem = {
  /** Image source URL */
  src: string;
  /** Alt text for accessibility */
  alt?: string;
};

export type ImageFallback = {
  /** Message to display when images unavailable */
  message: string;
  /** Action buttons */
  buttons?: ImageButton[];
};

export type AppWidgetImageProps = {
  /** App name displayed in header */
  appName: string;
  /** App icon: predefined key, URL string, or omit for letter avatar */
  appIcon?: PredefinedAppIcon | string;
  /** Images to display */
  images?: ImageItem[];
  /** Fallback content when images unavailable */
  fallback?: ImageFallback;
  /** Optional className */
  className?: string;
};

/**
 * Warning icon for fallback state
 */
const WarningIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="app-widget-image__warning-icon"
  >
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

/**
 * Fallback content component
 */
const FallbackContent: FC<{ fallback: ImageFallback }> = ({ fallback }) => (
  <div className="app-widget-image__fallback">
    <WarningIcon />
    <p className="app-widget-image__fallback-message">{fallback.message}</p>
    {fallback.buttons && fallback.buttons.length > 0 && (
      <div className="app-widget-image__fallback-buttons">
        {fallback.buttons.map((button, index) => (
          <button
            key={index}
            className={`app-widget-image__button app-widget-image__button--${button.variant || "secondary"}`}
            onClick={button.onClick}
          >
            {button.label}
          </button>
        ))}
      </div>
    )}
  </div>
);

/**
 * AppWidgetImage - Renders image previews (like Figma)
 *
 * @example
 * // With images
 * <AppWidgetImage
 *   appName="Figma"
 *   appIcon="figma"
 *   images={[{ src: "https://...", alt: "Design preview" }]}
 * />
 *
 * @example
 * // With fallback (expired/unavailable)
 * <AppWidgetImage
 *   appName="Figma"
 *   appIcon="figma"
 *   fallback={{
 *     message: "Assets have expired. To view, open in Figma.",
 *     buttons: [
 *       { label: "Open in Figma", variant: "primary" },
 *       { label: "Generate again", variant: "secondary" }
 *     ]
 *   }}
 * />
 */
export const AppWidgetImage: FC<AppWidgetImageProps> = ({
  appName,
  appIcon,
  images,
  fallback,
  className = "",
}) => {
  const hasImages = images && images.length > 0;

  return (
    <AppWidget appName={appName} appIcon={appIcon} className={className}>
      <div className="app-widget-image">
        {hasImages ? (
          <div className="app-widget-image__grid">
            {images.map((image, index) => (
              <img
                key={index}
                src={image.src}
                alt={image.alt || ""}
                className="app-widget-image__image"
              />
            ))}
          </div>
        ) : fallback ? (
          <FallbackContent fallback={fallback} />
        ) : null}
      </div>
    </AppWidget>
  );
};

/* ==========================================================================
   AppWidgetTrackList - Spotify tracks with footer CTA
   ========================================================================== */

export type TrackItem = {
  /** Track image/album art URL */
  image?: string;
  /** Track title */
  title: string;
  /** Artist/album info */
  subtitle?: string;
  /** Track duration (e.g., "3:24") */
  duration?: string;
};

export type AppWidgetTrackListProps = {
  /** App name displayed in header */
  appName: string;
  /** App icon: predefined key, URL string, or omit for letter avatar */
  appIcon?: PredefinedAppIcon | string;
  /** Track items to display */
  items: TrackItem[];
  /** Footer CTA button */
  footerCta?: { label: string; onClick?: () => void };
  /** Footer helper text */
  footerText?: string;
  /** Optional className */
  className?: string;
};

/**
 * Single track item row
 */
const TrackItemRow: FC<{ item: TrackItem; index: number }> = ({
  item,
  index,
}) => (
  <div className="app-widget-track-list__item">
    <span className="app-widget-track-list__index">{index + 1}</span>
    {item.image && (
      <img src={item.image} alt="" className="app-widget-track-list__image" />
    )}
    <div className="app-widget-track-list__text">
      <span className="app-widget-track-list__title">{item.title}</span>
      {item.subtitle && (
        <span className="app-widget-track-list__subtitle">{item.subtitle}</span>
      )}
    </div>
    {item.duration && (
      <span className="app-widget-track-list__duration">{item.duration}</span>
    )}
  </div>
);

/**
 * AppWidgetTrackList - Renders a track list with optional footer CTA
 */
export const AppWidgetTrackList: FC<AppWidgetTrackListProps> = ({
  appName,
  appIcon,
  items,
  footerCta,
  footerText,
  className = "",
}) => {
  return (
    <AppWidget appName={appName} appIcon={appIcon} className={className}>
      <div className="app-widget-track-list">
        <div className="app-widget-track-list__items">
          {items.map((item, index) => (
            <TrackItemRow key={index} item={item} index={index} />
          ))}
        </div>
        {(footerCta || footerText) && (
          <div className="app-widget-track-list__footer">
            {footerText && (
              <span className="app-widget-track-list__footer-text">
                {footerText}
              </span>
            )}
            {footerCta && (
              <button
                className="app-widget-track-list__cta"
                onClick={footerCta.onClick}
              >
                {footerCta.label}
              </button>
            )}
          </div>
        )}
      </div>
    </AppWidget>
  );
};

/* ==========================================================================
   AppWidgetCart - Instacart-style shopping cart
   ========================================================================== */

export type CartItem = {
  /** Item name */
  name: string;
  /** Quantity */
  quantity: number;
  /** Price string (e.g., "$4.99") */
  price: string;
  /** Item image URL */
  image?: string;
  /** Unit description (e.g., "28 oz can") */
  unit?: string;
};

export type AppWidgetCartProps = {
  /** App name displayed in header */
  appName: string;
  /** App icon: predefined key, URL string, or omit for letter avatar */
  appIcon?: PredefinedAppIcon | string;
  /** Cart items */
  items: CartItem[];
  /** Subtotal string */
  subtotal: string;
  /** Estimated delivery time */
  deliveryTime?: string;
  /** Primary CTA button */
  primaryCta?: { label: string; onClick?: () => void };
  /** Optional className */
  className?: string;
};

/**
 * Single cart item row
 */
const CartItemRow: FC<{ item: CartItem }> = ({ item }) => (
  <div className="app-widget-cart__item">
    {item.image && (
      <img src={item.image} alt="" className="app-widget-cart__item-image" />
    )}
    <div className="app-widget-cart__item-details">
      <span className="app-widget-cart__item-name">{item.name}</span>
      {item.unit && (
        <span className="app-widget-cart__item-unit">{item.unit}</span>
      )}
    </div>
    <span className="app-widget-cart__item-qty">x{item.quantity}</span>
    <span className="app-widget-cart__item-price">{item.price}</span>
  </div>
);

/**
 * AppWidgetCart - Renders a shopping cart with items and checkout CTA
 */
export const AppWidgetCart: FC<AppWidgetCartProps> = ({
  appName,
  appIcon,
  items,
  subtotal,
  deliveryTime,
  primaryCta,
  className = "",
}) => {
  return (
    <AppWidget appName={appName} appIcon={appIcon} className={className}>
      <div className="app-widget-cart">
        <div className="app-widget-cart__items">
          {items.map((item, index) => (
            <CartItemRow key={index} item={item} />
          ))}
        </div>
        <div className="app-widget-cart__footer">
          <div className="app-widget-cart__summary">
            <div className="app-widget-cart__subtotal">
              <span>Subtotal</span>
              <span>{subtotal}</span>
            </div>
            {deliveryTime && (
              <div className="app-widget-cart__delivery">
                <span>Est. delivery</span>
                <span>{deliveryTime}</span>
              </div>
            )}
          </div>
          {primaryCta && (
            <button
              className="app-widget-cart__cta"
              onClick={primaryCta.onClick}
            >
              {primaryCta.label}
            </button>
          )}
        </div>
      </div>
    </AppWidget>
  );
};

/* ==========================================================================
   AppWidgetConfirmation - Success/confirmation state
   ========================================================================== */

export type ConfirmationDetail = {
  /** Label (e.g., "ETA", "Address") */
  label: string;
  /** Value */
  value: string;
};

export type ConfirmationAction = {
  /** Button label */
  label: string;
  /** Button variant */
  variant?: "primary" | "secondary";
  /** Click handler */
  onClick?: () => void;
};

export type AppWidgetConfirmationProps = {
  /** App name displayed in header */
  appName: string;
  /** App icon: predefined key, URL string, or omit for letter avatar */
  appIcon?: PredefinedAppIcon | string;
  /** Confirmation title (e.g., "Playlist Created!") */
  title: string;
  /** Subtitle/description */
  subtitle?: string;
  /** Optional image (e.g., playlist cover) */
  image?: string;
  /** Key-value details */
  details?: ConfirmationDetail[];
  /** Action buttons */
  actions?: ConfirmationAction[];
  /** Optional className */
  className?: string;
};

/**
 * Check icon for success state
 */
const CheckCircleIcon: FC = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="app-widget-confirmation__check-icon"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

/**
 * AppWidgetConfirmation - Renders a success/confirmation state
 */
export const AppWidgetConfirmation: FC<AppWidgetConfirmationProps> = ({
  appName,
  appIcon,
  title,
  subtitle,
  image,
  details,
  actions,
  className = "",
}) => {
  return (
    <AppWidget appName={appName} appIcon={appIcon} className={className}>
      <div className="app-widget-confirmation">
        <div className="app-widget-confirmation__header">
          {image ? (
            <img
              src={image}
              alt=""
              className="app-widget-confirmation__image"
            />
          ) : (
            <CheckCircleIcon />
          )}
          <div className="app-widget-confirmation__title-area">
            <span className="app-widget-confirmation__title">{title}</span>
            {subtitle && (
              <span className="app-widget-confirmation__subtitle">
                {subtitle}
              </span>
            )}
          </div>
        </div>
        {details && details.length > 0 && (
          <div className="app-widget-confirmation__details">
            {details.map((detail, index) => (
              <div key={index} className="app-widget-confirmation__detail">
                <span className="app-widget-confirmation__detail-label">
                  {detail.label}
                </span>
                <span className="app-widget-confirmation__detail-value">
                  {detail.value}
                </span>
              </div>
            ))}
          </div>
        )}
        {actions && actions.length > 0 && (
          <div className="app-widget-confirmation__actions">
            {actions.map((action, index) => (
              <button
                key={index}
                className={`app-widget-confirmation__action app-widget-confirmation__action--${action.variant || "secondary"}`}
                onClick={action.onClick}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </AppWidget>
  );
};

/* ==========================================================================
   Exports
   ========================================================================== */

export type { PredefinedAppIcon };
